import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import {
  AgricolaAssistanceFirebaseStatus,
  AgricolaAssistanceSignatureFlow,
  AgricolaPaymentMode,
  SignEmissionLog
} from '../../../../../interfaces/agricola.interface';
import { AgricolaService } from '../../../../../services/agricola.service';
import { describeDocumentError } from '../../../../../utils/document-error.util';
import { SisSelectOption } from '../../../../sis/sis-select/sis-select.component';
import { AgricolaPaymentErrorDialogComponent } from './agricola-payment-error-dialog/agricola-payment-error-dialog.component';
import { agricolaPositiveIntegerValidator } from '../../../agricola-validators';

@Component({
  selector: 'app-agricola-payment-step',
  templateUrl: './agricola-payment-step.component.html',
  styleUrls: ['./agricola-payment-step.component.css'],
  standalone: false
})
export class AgricolaPaymentStepComponent implements OnInit, OnDestroy {

  @Input() group!: FormGroup;
  @Input() wizardGroup!: FormGroup;
  @Input() selectedIndex: number = 0;
  @Input() steps: string[] = [];
  
  @Output() nextStep = new EventEmitter<number>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() returnHome = new EventEmitter<void>();
  @Output() returnToSearch = new EventEmitter<void>();
  @Output() policyIssued = new EventEmitter<void>();
  @ViewChild('stepper') stepper!: any;

  submitting = false;
  loadingAccounts = false;
  accountsLoadedKey = 0;
  accountsLoadFailed = false;
  // Texto del estado fallido de los campos de cuenta: '' deja el genérico
  // "No se pudo cargar" (servicio caído); se llena cuando S2 responde sin cuentas.
  accountsLoadFailedText = '';

  loadingAgencies = false;
  agencyOptions: SisSelectOption[] = [];

  mode: AgricolaPaymentMode = 'form';

  // Seller type is a catalog (Interno = 1, Externo = 2) in the assistance flow.
  readonly sellerTypeOptions: SisSelectOption[] = [
    { label: 'Interno', value: '1' },
    { label: 'Externo', value: '2' }
  ];
  validatingSellerCode = false;
  sellerNameLoadFailed = false;
  private readonly subscriptions = new Subscription();

  // Accounts returned by /clients/accounts. When more than one is usable the
  // template renders a selector so the agent can pick which one funds the
  // policy; the chosen record is patched into the bank fields (and thus into
  // numCuenta/tipoCuenta sent to balance/validate).
  accountOptions: SisSelectOption[] = [];
  selectedAccountKey = '';
  private availableAccounts: Record<string, unknown>[] = [];
  // info quemada de resumen
  policyNumber = '00000000000000';
  // Número de proceso telemarketing (se muestra en la pantalla de éxito diferida).
  tmkProcessNumber = '';
  insuredName = 'Juan García Penagos Rottman';
  validityFrom = '00/MES/2024';
  validityTo = '00/MES/2024';

  documentLoading = false;
  documentError = '';
  // Se resuelve al mostrar la pantalla de éxito: sin documento no hay botón.
  policyDocumentAvailable = false;
  // Mientras se consulta si existe el documento el botón ya se renderiza, pero
  // deshabilitado y en estado de carga, para que el agente sepa que hay que
  // esperar en lugar de ver aparecer el botón de la nada.
  documentAvailabilityLoading = false;

  assistanceSignatureFlow: AgricolaAssistanceSignatureFlow = 'contingency';
  privateLinkSetupError = '';
  sendingPrivateLink = false;
  finalizingPrivateLink = false;
  assistanceEmissionLogId: number | null = null;
  assistanceToken = '';
  assistanceFinalUrl = '';
  assistanceDocumentUrl = '';
  assistanceAttachmentId: number | null = null;
  assistanceTransactionId = '';
  assistanceWaitMessage = '';
  // Seller-validated signature review (validate_signature_by_seller flow).
  // Kept separate from assistanceAttachmentId so the customer's signature is only
  // used for the review preview and never leaks into the policy-document button,
  // which must keep opening the policy PDF via downloadPolicy as before.
  signatureAttachmentId: number | null = null;
  signaturePreviewUrl: SafeUrl | null = null;
  signerName = '';
  signedAt = '';
  signatureLoading = false;
  signatureReviewError = '';
  // Feature 1: client_sign_emission_log audit shown alongside the signature preview.
  signEmissionLog: SignEmissionLog | null = null;
  signEmissionLogLoading = false;
  signEmissionLogError = '';
  resendCooldown = 0;
  private resendCooldownTimer: ReturnType<typeof setInterval> | null = null;
  private assistanceCompletionSubscription: Subscription | null = null;
  // Ocultamiento temporal de la firma digital: solo se ofrece firma física
  // (contingencia). Poner en true para volver a mostrar la opción; el flujo de
  // private-link sigue intacto detrás de esta bandera.
  readonly showDigitalSignatureOption = false;
  // Ocultamiento temporal de todo el bloque "Proceso de Firma": la asistencia se
  // emite sin subir el PDF de contingencia y sin enlace privado. Poner en true
  // para volver a mostrar la sección; ambos flujos siguen intactos detrás de esta
  // bandera (y del endpoint contingency-sign, que queda sin uso mientras tanto).
  readonly showSignatureProcessSection = false;
  readonly assistanceSignatureFlowOptions = [
    ...(this.showDigitalSignatureOption
      ? [{ label: 'Firma digital', value: 'private-link' as AgricolaAssistanceSignatureFlow }]
      : []),
    { label: 'Firma física', value: 'contingency' as AgricolaAssistanceSignatureFlow }
  ];

  // --- Assistance contingency (PDF upload) ---
  contingencyFileName = '';
  isDraggingContingencyFile = false;
  private contingencyPdf: Blob | null = null;
  // Descarga de la plantilla de firma física. Se hace a través del proxy
  // autenticado del backend (agricolaService.downloadContingencyTemplate), por lo
  // que el navegador nunca ve la URL real del bucket de GCS.
  downloadingTemplate = false;
  templateDownloadError = '';

  get isAssistanceProduct(): boolean {
    return this.agricolaService.isAssistanceProduct;
  }

  // Telemarketing mode: the final action records the sale as PENDING and
  // returns a process number instead of charging/issuing in place, so the
  // button reads "Generar número de proceso" rather than "Expedir".
  get isTmk(): boolean {
    return this.agricolaService.tmkMode;
  }

  get assistanceRequiresDigitalSignature(): boolean {
    return this.showSignatureProcessSection
      && this.isAssistanceProduct
      && this.agricolaService.requiresAssistanceDigitalSignature(this.wizardGroup?.getRawValue());
  }

  get usesAssistancePrivateLink(): boolean {
    return this.assistanceRequiresDigitalSignature && this.assistanceSignatureFlow === 'private-link';
  }

  get usesAssistanceContingency(): boolean {
    return this.assistanceRequiresDigitalSignature && this.assistanceSignatureFlow === 'contingency';
  }

  // When true the customer only signs from the public link and the seller emits
  // here after reviewing the signature; otherwise the legacy flow (customer signs
  // and emits) stays in effect.
  get usesSellerValidatedSignature(): boolean {
    return this.usesAssistancePrivateLink
      && this.agricolaService.validatesSignatureBySeller(this.wizardGroup?.getRawValue());
  }

  constructor(
    private readonly agricolaService: AgricolaService,
    private readonly dialog: MatDialog,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    if (!this.group) {
      throw new Error('AgricolaPaymentStepComponent requiere [group]');
    }

    this.configurePaymentModeValidators();
    this.configureAssistanceSignatureFlow();
    this.bindSellerCodeLookup();
    this.bindAgencyNameLookup();
    this.loadClientAccounts();
    this.loadAgencies();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.assistanceCompletionSubscription?.unsubscribe();
    if (this.resendCooldownTimer) {
      clearInterval(this.resendCooldownTimer);
      this.resendCooldownTimer = null;
    }
    this.clearSignaturePreview();
  }

  private clearSignaturePreview(): void {
    this.signaturePreviewUrl = null;
  }

  // atajos
  get paymentInfo(): FormGroup {
    return this.group.get('paymentInfo') as FormGroup;
  }

  get sellerInfo(): FormGroup {
    return this.group.get('sellerInfo') as FormGroup;
  }

  get bankData() { return this.paymentInfo.get('bankData'); }
  get accountType() { return this.paymentInfo.get('accountType'); }
  get accountNumber() { return this.paymentInfo.get('accountNumber'); }
  get accountName() { return this.paymentInfo.get('accountName'); }
  get accountStatus() { return this.paymentInfo.get('accountStatus'); }
  get autoDebitAuthorized() { return this.paymentInfo.get('autoDebitAuthorized'); }
  get acceptsMulticharge() { return this.paymentInfo.get('acceptsMulticharge'); }
  get cardLast4() { return this.paymentInfo.get('cardLast4'); }
  get paymentName() { return this.paymentInfo.get('paymentName'); }
  get cardAccountType() { return this.paymentInfo.get('cardAccountType'); }

  get sellerType() { return this.sellerInfo.get('sellerType'); }
  get sellerCode() { return this.sellerInfo.get('sellerCode'); }
  get sellerName() { return this.sellerInfo.get('sellerName'); }
  get agencyCode() { return this.sellerInfo.get('agencyCode'); }
  get agencyName() { return this.sellerInfo.get('agencyName'); }

  get insuredDisplayName(): string {
    const mainData = this.wizardGroup?.get('step1.mainData')?.value || {};
    const name = [
      mainData.firstName,
      mainData.middleName,
      mainData.firstLastName,
      mainData.secondLastName,
      mainData.marriedLastName
    ]
      .map((value: unknown) => value == null ? '' : String(value).trim())
      .filter((value: string) => value && value.toUpperCase() !== 'N/A')
      .join(' ');

    return name || 'Cliente';
  }

  get documentNumber(): string {
    const mainData = this.wizardGroup?.get('step1.mainData')?.value || {};
    return String(mainData.documentNumber || mainData.nit || '');
  }

  get voucherEmail(): string {
    return String(this.wizardGroup?.get('step1.complementary.email')?.value || '');
  }

  get customerPhoneForLink(): string {
    const complementary = this.wizardGroup?.get('step1.complementary')?.value || {};
    // Mismo criterio que usa el servicio para elegir el destino del SMS: un
    // número que es puro relleno de ceros no cuenta como teléfono.
    return this.agricolaService.normalizePhoneNumber(complementary.mobile)
      || this.agricolaService.normalizePhoneNumber(complementary.phone);
  }

  get accountTypeDisplay(): string {
    return this.accountTypeDisplayFromValue(this.accountType?.value);
  }

  get hasMultipleAccounts(): boolean {
    return this.accountOptions.length > 1;
  }

  get accountNumberDisplay(): string {
    const accountLast6 = this.extractLastDigits(this.accountNumber?.value, 6);
    return accountLast6 ? `****${accountLast6}` : '';
  }

  get accountNumberLabel(): string {
    return this.extractLast4Digits(this.cardLast4?.value) ? '*Número de tarjeta' : '*Número de cuenta';
  }

  // Cancelar: por ahora sólo resetea el formulario del paso
  onCancel(): void {
    this.group.reset({
      paymentInfo: {
        bankData: '',
        accountType: '',
        accountNumber: '',
        accountName: '',
        accountStatus: '',
        autoDebitAuthorized: false,
        acceptsMulticharge: false,
        cardLast4: '',
        paymentName: '',
        expirationDate: '',
        cardType: '',
        cardAccountType: ''
      },
      sellerInfo: {
        sellerType: '',
        sellerCode: '',
        sellerName: '',
        agencyCode: '',
        agencyName: ''
      }
    });
    this.mode = 'form';
    this.accountOptions = [];
    this.availableAccounts = [];
    this.selectedAccountKey = '';
    this.resetAssistancePrivateLinkState();
    this.configureAssistanceSignatureFlow();
    this.configurePaymentModeValidators();
  }

  // Expedir póliza
  async onSubmit(): Promise<void> {
    if (this.usesAssistancePrivateLink) {
      return;
    }

    if (this.mode !== 'form' || this.submitting) {
      return;
    }

    await this.submitBankPayment();
  }

  // Picks the sale path: assistance products with a captured contingency PDF go
  // through the contingency proxy (persisted upstream); everything else uses the
  // normal sale.
  private emitConfiguredSale(wizardValue: any): Promise<unknown> {
    if (this.isAssistanceProduct && this.usesAssistanceContingency && this.contingencyPdf) {
      return this.agricolaService.emitAssistanceSaleContingency(wizardValue, this.contingencyPdf);
    }
    return this.agricolaService.emitSale(wizardValue);
  }

  // Modo Telemarketing: deja la venta PENDIENTE (no cobra ni emite) y muestra el
  // process_number. Es el ÚNICO punto donde el flujo TMK difiere del F2F.
  private async handleTmkPending(wizardValue: any): Promise<void> {
    try {
      const res: any = await this.agricolaService.createTmkPending(wizardValue);
      const pendingError = this.extractSaleError(res);
      if (pendingError) {
        this.showEmissionError(pendingError);
        return;
      }
      // El BFF envuelve la respuesta en {data:{...}} (api_resource). Core pone
      // el process_number al tope de ese data; se soporta también la forma plana.
      const payload = (res && res.data) ? res.data : res;
      this.tmkProcessNumber =
        (payload && (payload.process_number || payload.processNumber)) || '';
      this.showTmkPending(wizardValue);
    } catch (error: any) {
      this.showEmissionError(this.extractErrorMessage(error));
    }
  }

  private showTmkPending(wizardValue: any): void {
    this.submitting = false;
    this.insuredName = this.insuredDisplayName;
    this.validityFrom = this.resolveValidityDate(wizardValue);
    this.validityTo = this.resolveValidityDate(wizardValue);
    this.mode = 'successTmk';
    // La apertura (diferida) terminó: salir ya no debe advertir.
    this.policyIssued.emit();
  }

  private async submitBankPayment(): Promise<void> {
    this.group.markAllAsTouched();

    if (this.group.invalid) {
      this.showRequiredFieldsError();
      return;
    }

    const wizardValue = this.wizardGroup.getRawValue();
    if (this.usesAssistanceContingency && !this.contingencyPdf) {
      this.showEmissionError('Adjunta el PDF de contingencia para continuar.');
      return;
    }
    // Fail-closed: con only_tdc_accounts activo la venta sólo puede cobrarse con
    // tarjeta. El filtro de collectUsableAccounts ya oculta las demás, pero esta
    // guarda impide emitir si por cualquier vía (patch previo, respuesta atípica)
    // el tipo elegido no es TDC.
    if (this.onlyTdcAccounts && !this.usesCreditCardPayment) {
      this.showEmissionError('Este producto solo admite pago con tarjeta de crédito.');
      return;
    }
    this.submitting = true;

    try {
      const sellerPayload = this.agricolaService.buildSellerValidationPayload(wizardValue);
      this.validateBalanceUnlessCreditCard(wizardValue).subscribe({
        next: balanceResponse => {
          this.agricolaService.validateSeller(sellerPayload).subscribe({
            next: async () => {
              try {
                if (this.isAssistanceProduct) {
                  await this.agricolaService.prevalidateAssistanceSale(wizardValue);
                }
                // Telemarketing: se mantienen balance, seller y prevalidación.
                // SOLO cambia la emisión: deja la venta pendiente, no cobra/emite.
                if (this.agricolaService.tmkMode) {
                  await this.handleTmkPending(wizardValue);
                  return;
                }
                const saleResponse = await this.emitConfiguredSale(wizardValue);
                const saleError = this.extractSaleError(saleResponse);
                if (saleError) {
                  this.showEmissionError(saleError);
                  return;
                }
                await this.issuePolicyAndShowSuccess(saleResponse, wizardValue, this.extractNumPoliza(balanceResponse));
              } catch (error: any) {
                this.showEmissionError(this.extractErrorMessage(error));
              }
            },
            error: error => this.showEmissionError(this.extractErrorMessage(error))
          });
        },
        error: error => this.showEmissionError(this.extractErrorMessage(error))
      });
    } catch (error: any) {
      this.submitting = false;
      this.showBusinessError(error?.message || 'No se pudo preparar el JSON de la venta.');
      this.mode = 'form';
    }
  }

  // Reenviar póliza por correo
  resendPolicy(): void {
    console.log('Reenviar póliza por correo para', this.policyNumber);
    // aquí podrías llamar a un servicio HTTP para reenviar el correo
  }

  // True once a real policy number has been issued (not the placeholder or the
  // "pending emission" state), so the document button is only offered when a PDF
  // can actually be produced.
  get hasIssuedPolicy(): boolean {
    const value = (this.policyNumber || '').trim();
    return Boolean(value) && value !== '00000000000000' && !value.toLowerCase().startsWith('pendiente');
  }

  // Con una póliza emitida el documento SIEMPRE existe en el backend (se genera
  // en la emisión y se envía por correo). El pre-chequeo hasPolicyDocument() es
  // frágil: un cold start del apiserver, una verificación justo tras emitir (PDF
  // aún renderizando), un 401 transitorio del interceptor o una red lenta del
  // cliente hacen que su GET lance y el catch lo degrade a `false`, ocultando un
  // documento que SÍ está. Esos fallos son "no pude verificar ahora", no "no
  // existe". Por eso el botón se muestra en cuanto hay póliza emitida; si en el
  // caso raro el PDF no se pudiera abrir, viewPolicyDocument() GET-valida al
  // hacer clic y muestra el error inline en vez de esconder la opción.
  get showPolicyDocumentButton(): boolean {
    return this.hasIssuedPolicy;
  }

  get policyDocumentBusy(): boolean {
    return this.documentLoading || this.documentAvailabilityLoading;
  }

  get policyDocumentButtonLabel(): string {
    if (this.documentAvailabilityLoading) {
      return 'BUSCANDO DOCUMENTO…';
    }
    return this.documentLoading ? 'GENERANDO…' : 'VER PÓLIZA (PDF)';
  }

  // Opens the policy document PDF in a new tab. The service GET-validates first,
  // so a generation failure surfaces here as an inline message rather than a
  // blank tab.
  async viewPolicyDocument(): Promise<void> {
    if (!this.hasIssuedPolicy || this.documentLoading) {
      return;
    }
    if (this.assistanceAttachmentId) {
      this.documentLoading = true;
      this.documentError = '';
      try {
        await this.agricolaService.viewAttachment(this.assistanceAttachmentId);
      } catch (error) {
        this.documentError = this.describeDocumentError(error);
      } finally {
        this.documentLoading = false;
      }
      return;
    }
    this.documentLoading = true;
    this.documentError = '';
    try {
      await this.agricolaService.downloadPolicy(this.policyNumber);
    } catch (error) {
      this.documentError = this.describeDocumentError(error);
    } finally {
      this.documentLoading = false;
    }
  }

  // Traduce el fallo del GET del documento a un mensaje accionable según su
  // causa, en vez de un genérico "no se pudo". El botón ahora SIEMPRE se muestra
  // con una póliza emitida (el documento existe en backend), así que cuando el
  // clic falla casi siempre es transitorio —cold start del apiserver, el PDF
  // todavía generándose, red del cliente— y el vendedor debe saber si reintentar,
  // esperar, o reportarlo con el número de póliza.
  private describeDocumentError(error: unknown): string {
    return describeDocumentError(error, this.policyNumber);
  }

  // -------------------------------------------------------------------------
  // Assistance contingency (in-app PDF upload)
  // -------------------------------------------------------------------------

  // Downloads the physical-signature template through the authenticated proxy so
  // the seller can print it, capture the client's signature and upload the PDF.
  async downloadContingencyTemplate(): Promise<void> {
    if (this.downloadingTemplate) {
      return;
    }
    this.downloadingTemplate = true;
    this.templateDownloadError = '';
    try {
      await this.agricolaService.downloadContingencyTemplate();
    } catch {
      this.templateDownloadError = 'No se pudo descargar la plantilla. Inténtalo de nuevo.';
    } finally {
      this.downloadingTemplate = false;
    }
  }

  // Captures the client's contingency PDF; when present an assistance sale is
  // routed through the contingency proxy (persisted upstream) on Expedir.
  onSelectContingencyPdf(event: Event): void {
    const file = (event.target as HTMLInputElement)?.files?.[0];
    if (file) {
      this.setContingencyFile(file);
    }
  }

  onContingencyDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingContingencyFile = true;
  }

  onContingencyDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingContingencyFile = false;
  }

  onContingencyDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingContingencyFile = false;
    const file = event.dataTransfer?.files?.[0];
    if (file && file.type === 'application/pdf') {
      this.setContingencyFile(file);
    }
  }

  private setContingencyFile(file: File): void {
    this.contingencyFileName = file.name;
    this.contingencyPdf = file;
  }

  onAssistanceSignatureFlowChange(flow: string): void {
    this.assistanceSignatureFlow = this.showDigitalSignatureOption && flow === 'private-link' ? 'private-link' : 'contingency';
  }

  async sendPrivateLinkByEmail(): Promise<void> {
    await this.startAssistancePrivateLinkFlow('email');
  }

  async sendPrivateLinkBySms(): Promise<void> {
    await this.startAssistancePrivateLinkFlow('sms');
  }

  private async startAssistancePrivateLinkFlow(channel: 'email' | 'sms'): Promise<void> {
    if (
      !this.usesAssistancePrivateLink
      || !['form', 'assistanceWaiting'].includes(this.mode)
      || this.sendingPrivateLink
      || this.finalizingPrivateLink
    ) {
      return;
    }

    this.group.markAllAsTouched();
    if (this.group.invalid) {
      this.showRequiredFieldsError();
      return;
    }

    if (channel === 'email' && !this.voucherEmail) {
      this.showEmissionError('Ingresa un correo electrónico para enviar el enlace de firma.');
      return;
    }

    if (channel === 'sms' && !this.customerPhoneForLink) {
      this.showEmissionError('Ingresa un teléfono o celular para enviar el enlace de firma.');
      return;
    }

    if (this.privateLinkSetupError) {
      this.showEmissionError(this.privateLinkSetupError);
      return;
    }

    const wizardValue = this.wizardGroup.getRawValue();
    this.sendingPrivateLink = true;
    this.submitting = true;

    try {
      await this.runSellerSideValidations(wizardValue);

      if (!this.assistanceEmissionLogId || !this.assistanceFinalUrl) {
        const state = await this.agricolaService.createAssistancePrivateLink(wizardValue, channel);
        this.assistanceEmissionLogId = state.emissionLogId;
        this.assistanceToken = state.token;
        this.assistanceFinalUrl = state.finalUrl;
      }

      if (channel === 'email') {
        await firstValueFrom(this.agricolaService.sendAssistancePrivateLinkEmail(wizardValue, this.assistanceFinalUrl));
      } else {
        await firstValueFrom(this.agricolaService.sendAssistancePrivateLinkSms(wizardValue, this.assistanceFinalUrl));
      }

      this.enterAssistanceWaitingState(channel);
    } catch (error: any) {
      this.showEmissionError(this.extractErrorMessage(error));
    } finally {
      this.sendingPrivateLink = false;
      this.submitting = false;
    }
  }

  // La validación de fondos no se ejecuta cuando se paga con TDC: se removió a
  // pedido del negocio. La prima de una tarjeta no la cobra el banco sino la
  // trama de billing de Agrícola (el collector que el producto configura en
  // `collector_tdc`, ingesta batch), así que el chequeo contra el maestro de
  // cuentas no aplica. Con cuenta bancaria queda
  // igual y sigue siendo fail-closed: si /balance/validate falla, no hay venta.
  //
  // Devuelve un observable de un solo valor en ambos casos para no romper la
  // cadena del emisor; en TDC emite null, y extractNumPoliza(null) da '', o sea
  // que el numPoliza de respaldo que salía del balance no existe para tarjetas
  // (el de la venta sigue siendo el que manda).
  private validateBalanceUnlessCreditCard(wizardValue: any): Observable<unknown> {
    if (this.usesCreditCardPayment) {
      return of(null);
    }

    return this.agricolaService.validateBalance(
      this.agricolaService.buildBalanceValidationPayload(wizardValue)
    );
  }

  private get usesCreditCardPayment(): boolean {
    return this.agricolaService.isCreditCardAccount(this.accountType?.value);
  }

  private async runSellerSideValidations(wizardValue: any): Promise<void> {
    await firstValueFrom(this.validateBalanceUnlessCreditCard(wizardValue));
    await firstValueFrom(this.agricolaService.validateSeller(
      this.agricolaService.buildSellerValidationPayload(wizardValue)
    ));
    if (this.isAssistanceProduct) {
      await this.agricolaService.prevalidateAssistanceSale(wizardValue);
    }
  }

  private enterAssistanceWaitingState(channel: 'email' | 'sms'): void {
    if (!this.assistanceEmissionLogId) {
      this.showEmissionError('No se pudo preparar la espera de la asistencia con firma.');
      return;
    }

    this.mode = 'assistanceWaiting';
    const via = channel === 'email' ? 'al correo del cliente' : 'por SMS al cliente';
    this.assistanceWaitMessage = this.usesSellerValidatedSignature
      ? `Enlace enviado ${via}. Esperando la firma del cliente para validarla y emitir.`
      : `Enlace enviado ${via}. Esperando la firma y emisión.`;
    this.startResendCooldown();
    this.subscribeToAssistanceCompletion(this.assistanceEmissionLogId);
  }

  private subscribeToAssistanceCompletion(emissionLogId: number): void {
    this.assistanceCompletionSubscription?.unsubscribe();
    const sellerValidated = this.usesSellerValidatedSignature;
    this.assistanceCompletionSubscription = this.agricolaService.watchAssistancePrivateLinkCompletion(emissionLogId)
      .subscribe({
        next: status => {
          if (sellerValidated) {
            // Seller emits: the customer only reaches status/stage "signed"; a
            // status 1 here only happens on a mid-flow reload of an already
            // emitted link.
            if (status.status === 1 || status.stage === 'emitted') {
              void this.finalizeAssistancePrivateLinkSuccess(status);
            } else if (status.status === 2 || status.stage === 'signed') {
              this.enterSignatureReviewState(status);
            }
            return;
          }

          // Legacy flow: the customer signs and emits, we just register locally.
          if (status.status === 1) {
            void this.finalizeAssistancePrivateLinkSuccess(status);
          }
        },
        error: error => {
          this.assistanceWaitMessage = this.extractErrorMessage(error)
            || 'No se pudo escuchar la finalización en tiempo real. Reenvía el enlace o usa contingencia.';
        }
      });
  }

  // The customer signed from the public link; switch to the review screen and load
  // the signature so the seller can validate it before emitting.
  private enterSignatureReviewState(status: AgricolaAssistanceFirebaseStatus): void {
    if (this.finalizingPrivateLink || !['assistanceWaiting', 'assistanceSignatureReview'].includes(this.mode)) {
      return;
    }

    this.mode = 'assistanceSignatureReview';
    this.signatureAttachmentId = status.attachmentId;
    this.assistanceTransactionId = status.transactionId;
    this.signerName = status.signerName || this.insuredDisplayName;
    this.signedAt = this.formatSignedAt(status.signedAt);

    if (status.attachmentId && !this.signaturePreviewUrl) {
      void this.loadSignaturePreview(status.attachmentId);
    }

    if (this.assistanceEmissionLogId && !this.signEmissionLog) {
      void this.loadSignEmissionLog(this.assistanceEmissionLogId);
    }
  }

  // Feature 1: fetch the client_sign_emission_log audit (IP, signed_at, sent-to,
  // attachment ids, …) so the seller can review the signature's provenance before
  // emitting. Non-blocking: a failure only hides the audit block, the seller can
  // still validate/emit and retry the load.
  private async loadSignEmissionLog(emissionLogId: number): Promise<void> {
    this.signEmissionLogLoading = true;
    this.signEmissionLogError = '';
    try {
      this.signEmissionLog = await this.agricolaService.getSignEmissionLog(emissionLogId);
    } catch {
      this.signEmissionLog = null;
      this.signEmissionLogError = 'No se pudo cargar la información de la firma. Reintenta la carga.';
    } finally {
      this.signEmissionLogLoading = false;
    }
  }

  retryLoadSignEmissionLog(): void {
    if (this.assistanceEmissionLogId) {
      void this.loadSignEmissionLog(this.assistanceEmissionLogId);
    }
  }

  // Spanish labels for the known client_sign_emission_log fields. Anything S2 adds
  // later still renders (falls back to the raw key), honoring "todo lo disponible".
  private static readonly SIGN_EMISSION_LOG_LABELS: Record<string, string> = {
    client_ip: 'IP del cliente',
    signed_at: 'Fecha y hora de firma',
    sent_channel: 'Canal de envío',
    sent_to_email: 'Enviado a (correo)',
    sent_to_phone: 'Enviado a (teléfono)',
    sent_at: 'Fecha de envío',
    holder_national_id: 'Identificación del titular',
    policy_number: 'No. de póliza',
    signature_attachment_id: 'ID adjunto (firma)',
    signed_document_attachment_id: 'ID adjunto (documento firmado)',
    emission_log_id: 'ID de emisión',
    token: 'Token',
    status: 'Estado',
    created_at: 'Creado',
    modify_date: 'Modificado',
  };

  // Every populated field of the audit record as {label, value} rows so the template
  // can render "todo lo disponible" without hardcoding the column list.
  get signEmissionLogEntries(): { label: string; value: string }[] {
    const log = this.signEmissionLog;
    if (!log) {
      return [];
    }
    return Object.keys(log)
      .filter(key => key !== 'id')
      .map(key => ({
        key,
        label: AgricolaPaymentStepComponent.SIGN_EMISSION_LOG_LABELS[key] || key,
        value: this.formatSignEmissionLogValue(key, (log as Record<string, unknown>)[key]),
      }))
      .filter(entry => entry.value !== '')
      .map(({ label, value }) => ({ label, value }));
  }

  private formatSignEmissionLogValue(key: string, value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    if (key === 'status') {
      const labels: Record<string, string> = { 0: 'Pendiente', 1: 'Firmado', 2: 'Emitido' };
      return labels[String(value)] || String(value);
    }
    if (key === 'signed_at') {
      return this.formatSignedAt(value);
    }
    return String(value);
  }

  // Presenta la marca de firma (ISO 2026-07-20T17:24:40.180508) como
  // dd/MM/yyyy HH:mm. Si el valor no es una fecha parseable se deja intacto.
  private formatSignedAt(value: unknown): string {
    const raw = String(value ?? '').trim();
    if (!raw) {
      return '';
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return raw;
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    const dd = pad(date.getDate());
    const mm = pad(date.getMonth() + 1);
    const hh = pad(date.getHours());
    const min = pad(date.getMinutes());
    return `${dd}/${mm}/${date.getFullYear()} ${hh}:${min}`;
  }

  private async loadSignaturePreview(attachmentId: number): Promise<void> {
    this.signatureLoading = true;
    this.signatureReviewError = '';
    try {
      // data: URL (not blob:) — the app CSP allows img-src data: but not blob:.
      const dataUrl = await this.agricolaService.fetchAttachmentDataUrl(attachmentId);
      this.signaturePreviewUrl = this.sanitizer.bypassSecurityTrustUrl(dataUrl);
    } catch {
      this.signatureReviewError = 'No se pudo cargar la firma del cliente. Reintenta la carga.';
    } finally {
      this.signatureLoading = false;
    }
  }

  retryLoadSignaturePreview(): void {
    if (this.signatureAttachmentId) {
      void this.loadSignaturePreview(this.signatureAttachmentId);
    }
  }

  // Seller validates the signature and emits. The upstream emit reuses the stored
  // signature; on success we flip Firebase to "emitted" (releasing the customer's
  // waiting page), register the create-policy payload locally, and show the summary.
  async validateAndEmit(): Promise<void> {
    if (this.finalizingPrivateLink || this.mode !== 'assistanceSignatureReview' || !this.assistanceToken) {
      return;
    }

    this.finalizingPrivateLink = true;
    this.submitting = true;
    this.signatureReviewError = '';

    const wizardValue = this.wizardGroup.getRawValue();
    try {
      const response = await this.agricolaService.emitAssistancePrivateLink(this.assistanceToken);
      const result = this.agricolaService.extractEmissionResult(response);
      const policyNumber = result.policyNumber || result.transactionId || 'Pendiente de emisión';

      this.assistanceDocumentUrl = result.documentUrl;
      this.assistanceTransactionId = result.transactionId;

      if (this.assistanceEmissionLogId) {
        try {
          await this.agricolaService.writeAssistanceEmissionResult(this.assistanceEmissionLogId, result);
        } catch {
          // Non-fatal: the sale is emitted upstream (source of truth is
          // EmissionLog.status). The customer's live page may lag until reload.
          this.documentError = 'La asistencia se emitió, pero no se pudo actualizar la vista del cliente en tiempo real.';
        }
      }

      if (policyNumber && policyNumber !== 'Pendiente de emisión') {
        try {
          await this.agricolaService.emitPolicy(wizardValue, policyNumber);
        } catch {
          this.documentError = 'La asistencia se emitió, pero no se pudo registrar localmente el documento.';
        }
      }

      this.showSuccess(policyNumber, wizardValue);
    } catch (error: any) {
      // Keep the review screen so the seller can retry.
      this.signatureReviewError = this.extractErrorMessage(error);
    } finally {
      this.finalizingPrivateLink = false;
      this.submitting = false;
    }
  }

  // Seller rejects the reviewed signature: reset the link upstream (status 2 -> 0),
  // flip Firebase to pending (returns the customer to the signature step), and go
  // back to waiting for the new signature. The completion watcher re-fires on the
  // next 'signed' and reopens the review.
  async rejectSignature(): Promise<void> {
    if (this.finalizingPrivateLink || this.mode !== 'assistanceSignatureReview' || !this.assistanceToken) {
      return;
    }

    this.finalizingPrivateLink = true;
    this.signatureReviewError = '';
    const reason = 'No validada';

    try {
      await this.agricolaService.rejectAssistancePrivateLink(this.assistanceToken, reason);

      if (this.assistanceEmissionLogId) {
        try {
          await this.agricolaService.writeAssistanceRejection(this.assistanceEmissionLogId, reason);
        } catch {
          // Non-fatal: EmissionLog.status=0 is the source of truth; a reload recovers.
        }
      }

      this.signatureAttachmentId = null;
      this.clearSignaturePreview();
      this.mode = 'assistanceWaiting';
      this.assistanceWaitMessage = 'Firma rechazada. Esperando que el cliente vuelva a firmar.';
    } catch (error: any) {
      this.signatureReviewError = this.extractErrorMessage(error);
    } finally {
      this.finalizingPrivateLink = false;
    }
  }

  private async finalizeAssistancePrivateLinkSuccess(status: AgricolaAssistanceFirebaseStatus): Promise<void> {
    if (this.finalizingPrivateLink || !['assistanceWaiting', 'assistanceSignatureReview'].includes(this.mode)) {
      return;
    }

    this.finalizingPrivateLink = true;
    this.assistanceDocumentUrl = status.documentUrl;
    // In the seller-validated flow the node still carries the signature's
    // attachment_id (Firebase update merges the "signed" write), which must not
    // drive the policy-document button — that keeps opening the policy PDF via
    // downloadPolicy. Only the legacy flow may surface an emitted attachment.
    this.assistanceAttachmentId = this.usesSellerValidatedSignature ? null : status.attachmentId;
    this.assistanceTransactionId = status.transactionId;

    const wizardValue = this.wizardGroup.getRawValue();
    const policyNumber = status.policyNumber || status.transactionId || 'Pendiente de emisión';

    try {
      if (policyNumber && policyNumber !== 'Pendiente de emisión') {
        try {
          await this.agricolaService.emitPolicy(wizardValue, policyNumber);
        } catch {
          this.documentError = 'La asistencia se emitió, pero no se pudo registrar localmente el documento.';
        }
      }
      this.showSuccess(policyNumber, wizardValue);
    } finally {
      this.finalizingPrivateLink = false;
    }
  }

  private startResendCooldown(): void {
    if (this.resendCooldownTimer) {
      clearInterval(this.resendCooldownTimer);
    }

    this.resendCooldown = 30;
    this.resendCooldownTimer = setInterval(() => {
      if (this.resendCooldown <= 1) {
        clearInterval(this.resendCooldownTimer as ReturnType<typeof setInterval>);
        this.resendCooldownTimer = null;
        this.resendCooldown = 0;
        return;
      }

      this.resendCooldown -= 1;
    }, 1000);
  }

  private configureAssistanceSignatureFlow(): void {
    this.privateLinkSetupError = this.agricolaService.assistancePrivateLinkSetupError();
    this.assistanceSignatureFlow = this.showDigitalSignatureOption
      ? this.agricolaService.defaultAssistanceSignatureFlow(this.wizardGroup?.getRawValue())
      : 'contingency';
  }

  private resetAssistancePrivateLinkState(): void {
    this.assistanceCompletionSubscription?.unsubscribe();
    this.assistanceCompletionSubscription = null;
    if (this.resendCooldownTimer) {
      clearInterval(this.resendCooldownTimer);
      this.resendCooldownTimer = null;
    }
    this.assistanceEmissionLogId = null;
    this.assistanceToken = '';
    this.assistanceFinalUrl = '';
    this.assistanceDocumentUrl = '';
    this.assistanceAttachmentId = null;
    this.assistanceTransactionId = '';
    this.assistanceWaitMessage = '';
    this.signerName = '';
    this.signedAt = '';
    this.signatureAttachmentId = null;
    this.signatureLoading = false;
    this.signatureReviewError = '';
    this.signEmissionLog = null;
    this.signEmissionLogLoading = false;
    this.signEmissionLogError = '';
    this.clearSignaturePreview();
    this.resendCooldown = 0;
    this.privateLinkSetupError = '';
    this.finalizingPrivateLink = false;
  }

  goBack(): void {
    if (this.selectedIndex > 0 && this.stepper) {
      this.selectedIndex--;
      this.stepper.selectedIndex = this.selectedIndex;
    }
  }

  goToStep(index: number): void {
    console.log('goToStep', index);
    if (index <= this.selectedIndex && this.stepper) {
      this.selectedIndex = index;
      this.stepper.selectedIndex = this.selectedIndex;
    }
  }

  private resolveValidityDate(wizardValue: any): string {
    return String(wizardValue?.step1?.complementary?.effectiveDate || new Date().toISOString().slice(0, 10));
  }

  retryLoadAccounts(): void { this.loadClientAccounts(); }

  private loadAgencies(): void {
    this.loadingAgencies = true;
    this.agricolaService.getAgencies().subscribe({
      next: response => {
        this.loadingAgencies = false;
        const rows = this.extractRows(response);
        this.agencyOptions = rows
          .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object' && !Array.isArray(row))
          .map(row => ({
            value: this.firstString(row, ['code']),
            label: this.firstString(row, ['description_es', 'description_en', 'code'])
          }))
          .filter(opt => opt.value)
          // Ordenadas alfabéticamente por nombre para facilitar la búsqueda en el select.
          .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
        // Agencies load async: resolve the name for the code already selected.
        this.syncAgencyName(String(this.agencyCode?.value ?? ''));
      },
      error: () => { this.loadingAgencies = false; }
    });
  }

  private loadClientAccounts(): void {
    if (!this.wizardGroup) {
      return;
    }

    const wizardValue = this.wizardGroup.getRawValue();
    const clientUniqueNumber = this.agricolaService.resolveClientUniqueNumberForWizard(wizardValue);

    if (!clientUniqueNumber) {
      this.clearAccountData();
      return;
    }

    this.loadingAccounts = true;
    this.accountsLoadFailed = false;
    this.accountsLoadFailedText = '';
    this.agricolaService.getClientAccounts(clientUniqueNumber).subscribe({
      next: response => {
        this.loadingAccounts = false;
        const accounts = this.collectUsableAccounts(response);
        if (accounts.length) {
          this.setAvailableAccounts(accounts);
          this.accountsLoadedKey++;
        } else {
          // Sin cuentas no hay con qué cobrar: se muestra como fallo de carga
          // para que el vendedor vea el motivo y pueda reintentar. Con el filtro
          // only_tdc_accounts activo el motivo es específico: el cliente puede
          // tener cuentas, pero ninguna tarjeta de crédito.
          this.accountsLoadFailed = true;
          this.accountsLoadFailedText = this.onlyTdcAccounts
            ? 'El cliente no tiene tarjetas de crédito disponibles'
            : 'El cliente no tiene cuentas disponibles';
          this.clearAccountData();
        }
      },
      error: error => {
        this.loadingAccounts = false;
        this.accountsLoadFailed = true;
        this.clearAccountData();
        this.showBusinessError(this.extractErrorMessage(error));
      }
    });
  }

  // Sin cuentas del cliente (servicio caído, respuesta vacía o sin número único)
  // los campos bancarios quedan vacíos: son readonly y required, así que el
  // vendedor sólo avanza reintentando la consulta, nunca con una cuenta previa.
  private clearAccountData(): void {
    this.accountOptions = [];
    this.availableAccounts = [];
    this.selectedAccountKey = '';
    this.paymentInfo.patchValue({
      bankData: '',
      accountType: '',
      accountNumber: '',
      accountName: '',
      accountStatus: '',
      cardLast4: '',
      cardAccountType: '',
      paymentName: ''
    }, { emitEvent: false });
  }

  // Returns every account row that carries a usable identifier (a card code or
  // an account number), preserving the order received from S2. When the selected
  // product declares only_tdc_accounts, non-card rows are dropped so the seller
  // can only fund the policy with a credit card (Antifraude+ is charged through
  // the TDC billing trama; a bank account never applies).
  private collectUsableAccounts(response: unknown): Record<string, unknown>[] {
    const rows = this.extractRows(response);
    const usable = rows.filter((row): row is Record<string, unknown> => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        return false;
      }
      const record = row as Record<string, unknown>;
      return !!this.firstString(record, ['codCtaTDC'])
        || !!this.firstString(record, ['numCta', 'numCuenta', 'accountNumber', 'account_number']);
    });

    if (!this.onlyTdcAccounts) {
      return usable;
    }

    return usable.filter(account => this.isCreditCardAccountRow(account));
  }

  // True when the selected product restricts payment to credit-card accounts.
  private get onlyTdcAccounts(): boolean {
    return this.agricolaService.onlyTdcAccounts(this.wizardGroup?.getRawValue());
  }

  // A row is a credit card ONLY when its account-type code resolves to the
  // canonical TDC code (isCreditCardAccount is the single source of truth used by
  // tipoCuenta/tipoCobro and the funds check). NOTE: codCtaTDC is NOT a reliable
  // signal — S2 populates it on savings accounts too (e.g. "CTA.DE AHORROS DE
  // PENSIONADOS" arrives with codTipoCta:"2" and a codCtaTDC). Only codTipoCta:"3"
  // (or a tipoCta label that says tarjeta/crédito/tdc) is a real card.
  private isCreditCardAccountRow(account: Record<string, unknown>): boolean {
    const accountTypeCode = this.firstString(account, [
      'codTipoCta', 'accountTypeCode', 'account_type_code', 'codigoTipoCuenta'
    ]);
    const accountTypeLabel = this.firstString(account, [
      'tipoCta', 'tipoCuenta', 'accountType', 'account_type', 'tipo_cuenta', 'type'
    ]);
    return this.agricolaService.isCreditCardAccount(accountTypeCode || accountTypeLabel);
  }

  // Builds the selector options, defaults to the preferred account (first card,
  // else first usable) and patches the bank fields from it. The selector is only
  // shown when more than one account is available; a single account keeps the
  // previous auto-fill behaviour.
  private setAvailableAccounts(accounts: Record<string, unknown>[]): void {
    this.availableAccounts = accounts;
    this.accountOptions = accounts.map((account, index) => ({
      value: String(index),
      label: this.accountOptionLabel(account)
    }));

    const defaultIndex = this.pickDefaultAccountIndex(accounts);
    this.selectedAccountKey = String(defaultIndex);
    this.patchAccountFromResponse(accounts[defaultIndex]);
  }

  private pickDefaultAccountIndex(accounts: Record<string, unknown>[]): number {
    const cardIndex = accounts.findIndex(account => !!this.firstString(account, ['codCtaTDC']));
    return Math.max(cardIndex, 0);
  }

  // Labels each option with the account number (the identifier actually sent to
  // balance/validate); the card code is only used when the row has no number.
  private accountOptionLabel(account: Record<string, unknown>): string {
    const typeLabel = this.firstString(account, [
      'tipoCta', 'tipoCuenta', 'accountType', 'account_type', 'tipo_cuenta', 'type'
    ]) || 'Cuenta';
    const accountNumber = this.firstString(account, [
      'numCta', 'numCuenta', 'accountNumber', 'account_number', 'numeroCuenta', 'numero_cuenta', 'cuenta', 'number'
    ]);
    const cardLast4 = this.extractLast4Digits(this.firstString(account, ['codCtaTDC']));
    let masked = '';
    if (accountNumber) {
      masked = `***${this.extractLastDigits(accountNumber, 6)}`;
    } else if (cardLast4) {
      masked = `****${cardLast4}`;
    }
    return masked ? `${typeLabel} ${masked}` : typeLabel;
  }

  // Re-patches the bank fields when the agent picks a different account.
  onAccountSelected(key: string): void {
    const account = this.availableAccounts[Number(key)];
    if (account) {
      this.selectedAccountKey = key;
      this.patchAccountFromResponse(account);
    }
  }

  private extractRows(value: unknown): unknown[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (!value || typeof value !== 'object') {
      return [];
    }

    const record = value as Record<string, unknown>;
    const body = record['body'];

    if (Array.isArray(body)) {
      return body;
    }

    const data = record['data'];

    if (Array.isArray(data)) {
      return data;
    }

    if (data && typeof data === 'object') {
      const dataRecord = data as Record<string, unknown>;
      const dataBody = dataRecord['body'];
      if (Array.isArray(dataBody)) {
        return dataBody;
      }

      const nestedData = dataRecord['data'];
      if (Array.isArray(nestedData)) {
        return nestedData;
      }
      if (nestedData && typeof nestedData === 'object') {
        const nestedBody = (nestedData as Record<string, unknown>)['body'];
        if (Array.isArray(nestedBody)) {
          return nestedBody;
        }
      }
      return [data];
    }

    return [];
  }

  private patchAccountFromResponse(account: Record<string, unknown>): void {
    const cardLast4 = this.extractLast4Digits(this.firstString(account, ['codCtaTDC']));
    const accountTypeCode = this.firstString(account, [
      'codTipoCta',
      'accountTypeCode',
      'account_type_code',
      'codigoTipoCuenta'
    ]);
    const accountTypeLabel = this.firstString(account, [
      'tipoCta',
      'tipoCuenta',
      'accountType',
      'account_type',
      'tipo_cuenta',
      'type'
    ]);
    const accountNumber = this.firstString(account, [
      'numCta',
      'numCuenta',
      'accountNumber',
      'account_number',
      'numeroCuenta',
      'numero_cuenta',
      'cuenta',
      'number'
    ]);
    const accountName = this.firstString(account, [
      'nombreCta',
      'nombreCuenta',
      'accountName',
      'account_name',
      'nombre',
      'name'
    ]);
    const accountStatus = this.firstString(account, [
      'estadoCta',
      'estadoCuenta',
      'accountStatus',
      'account_status',
      'estado',
      'status'
    ]);
    const accountTypeValue = accountTypeCode || this.accountTypeCodeFromValue(accountTypeLabel) || accountTypeLabel;
    const bankDataLabel = accountTypeLabel || this.accountTypeDisplayFromValue(accountTypeValue) || 'Cuenta';
    const bankData = accountNumber
      ? `${bankDataLabel} ***${this.extractLastDigits(accountNumber, 6)}`
      : '';

    // Cada campo sale sólo de la cuenta elegida: sin respaldo al valor previo,
    // que arrastraba datos de otra cuenta al cambiar la selección.
    this.paymentInfo.patchValue({
      bankData,
      accountType: accountTypeValue,
      accountNumber,
      accountName,
      accountStatus,
      cardLast4,
      cardAccountType: cardLast4 ? accountTypeValue : '',
      paymentName: cardLast4 ? accountName : ''
    }, { emitEvent: false });
  }

  private firstString(record: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = record[key];
      if (value != null && String(value).trim()) {
        return String(value).trim();
      }
    }

    return '';
  }

  private configurePaymentModeValidators(): void {
    const bankControls = [
      this.accountType,
      this.accountNumber,
      this.accountName,
      this.accountStatus
    ];

    bankControls.forEach(control => this.setRequired(control, true));
    // "Datos bancarios" no se muestra en ningún flujo, así que no puede bloquear
    // el submit.
    this.setRequired(this.bankData, false);
    [this.sellerType, this.sellerName]
      .forEach(control => this.setRequired(control, false));
    // El código de vendedor no es obligatorio aquí, pero cuando se captura debe
    // ser un entero positivo: sin decimales, negativos ni exponenciales.
    this.sellerCode?.setValidators(agricolaPositiveIntegerValidator('invalidSellerCode'));
    this.sellerCode?.updateValueAndValidity({ emitEvent: false });
    this.setRequired(this.agencyCode, true);

    // autoDebitAuthorized uses requiredTrue because false is a valid form value
    // and Validators.required would not catch an unchecked checkbox.
    this.autoDebitAuthorized?.setValidators(Validators.requiredTrue);
    this.autoDebitAuthorized?.updateValueAndValidity({ emitEvent: false });
    this.acceptsMulticharge?.setValidators(null);
    this.acceptsMulticharge?.updateValueAndValidity({ emitEvent: false });
  }

  // Typing the seller code triggers the same seller validation used at emission
  // y prefills the seller name from the response (data.body.nombreVendedor).
  // Aplica a asistencias y seguros por igual: el endpoint no distingue producto y
  // el campo de nombre es readonly en ambos. La validación al emitir no cambia.
  private bindSellerCodeLookup(): void {
    if (!this.sellerCode) {
      return;
    }

    this.subscriptions.add(
      this.sellerCode.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged()
      ).subscribe(code => this.prefillSellerName(String(code ?? '')))
    );
  }

  // Keeps sellerInfo.agencyName in sync with the selected agencyCode so the agency
  // name (not just the code) is carried to the emission payload (additionalData.location_name).
  private bindAgencyNameLookup(): void {
    if (!this.agencyCode) {
      return;
    }

    this.subscriptions.add(
      this.agencyCode.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe(code => this.syncAgencyName(String(code ?? '')))
    );
  }

  private syncAgencyName(code: string): void {
    const label = this.agencyOptions.find(opt => opt.value === code.trim())?.label ?? '';
    this.agencyName?.setValue(label, { emitEvent: false });
  }

  retrySellerNameLookup(): void {
    if (!this.sellerCode?.value) {
      return;
    }
    this.prefillSellerName(String(this.sellerCode.value));
  }

  private prefillSellerName(code: string): void {
    const trimmed = code.trim();
    this.sellerNameLoadFailed = false;
    if (!trimmed) {
      this.sellerName?.setValue('', { emitEvent: false });
      return;
    }

    this.validatingSellerCode = true;
    const payload = {
      codigoVendedor: trimmed,
      tipoVendedor: String(this.sellerType?.value ?? '')
    };

    this.agricolaService.validateSeller(payload).subscribe({
      next: response => {
        this.validatingSellerCode = false;
        const name = this.agricolaService.extractSellerName(response);
        if (name) {
          this.sellerName?.setValue(name, { emitEvent: false });
        }
      },
      error: () => {
        this.validatingSellerCode = false;
        this.sellerNameLoadFailed = true;
      }
    });
  }

  private setRequired(control: AbstractControl | null, required: boolean): void {
    if (!control) {
      return;
    }

    control.setValidators(required ? Validators.required : null);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private accountTypeDisplayFromValue(value: unknown): string {
    const code = this.accountTypeCodeFromValue(value);
    const labels: Record<string, string> = {
      '1': 'Cuenta Corriente',
      '2': 'Cuenta Ahorro',
      '3': 'Tarjeta de crédito'
    };

    return code ? `${code} - ${labels[code]}` : this.firstDisplayValue(value);
  }

  private accountTypeCodeFromValue(value: unknown): string {
    const raw = this.firstDisplayValue(value);
    const normalized = raw
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    // Accepts CodTipoCta codes 1 (Corriente), 2 (Ahorro), 3 (TDC) from the accounts endpoint
    const leadingCode = normalized.match(/^[123]\b/)?.[0];

    if (leadingCode) {
      return leadingCode;
    }

    if (normalized.includes('credit') || normalized.includes('tarjeta') || normalized.includes('tdc')) {
      return '3';
    }

    if (normalized.includes('ahorro')) {
      return '2';
    }

    if (normalized.includes('cuenta') || normalized.includes('corriente')) {
      return '1';
    }

    return '';
  }

  private firstDisplayValue(value: unknown): string {
    return value == null ? '' : String(value).trim();
  }

  private extractLast4Digits(value: unknown): string {
    return (value == null ? '' : String(value)).replace(/\D/g, '').slice(-4);
  }

  private extractLastDigits(value: unknown, length: number): string {
    return (value == null ? '' : String(value)).replace(/\D/g, '').slice(-length);
  }

  // Records the create-policy payload (/ext/agricola/policies) right after the
  // sale and, once it is persisted, switches to the policy-number screen.
  // numPoliza (the partner-generated policy number) comes from the sale response
  // and is stored with the payload for the later replay to SIS.
  private async issuePolicyAndShowSuccess(
    saleResponse: unknown,
    wizardValue: any,
    fallbackPolicyNumber: string
  ): Promise<void> {
    const numPoliza = this.extractNumPoliza(saleResponse) || fallbackPolicyNumber;

    if (!numPoliza) {
      // Sale succeeded but no policy number came back yet; there is nothing to
      // register with SIS, so surface the pending state without calling policies.
      this.showSuccess('Pendiente de emisión', wizardValue);
      return;
    }

    const policyResponse = await this.agricolaService.emitPolicy(wizardValue, numPoliza);
    const policyError = this.extractSaleError(policyResponse);
    if (policyError) {
      this.showEmissionError(policyError);
      return;
    }

    this.showSuccess(numPoliza, wizardValue);
  }

  private showSuccess(policyNumber: string, wizardValue: any): void {
    this.submitting = false;
    this.assistanceCompletionSubscription?.unsubscribe();
    this.assistanceCompletionSubscription = null;
    if (this.resendCooldownTimer) {
      clearInterval(this.resendCooldownTimer);
      this.resendCooldownTimer = null;
    }
    this.resendCooldown = 0;
    this.policyNumber = policyNumber;
    this.insuredName = this.insuredDisplayName;
    this.validityFrom = this.resolveValidityDate(wizardValue);
    this.validityTo = this.resolveValidityDate(wizardValue);
    this.mode = 'successPaid';
    void this.refreshPolicyDocumentAvailability();
    // The apertura is finished: tell the wizard so leaving no longer prompts.
    this.policyIssued.emit();
  }

  // El botón "Ver póliza" solo aparece cuando hay documento de verdad: el
  // adjunto firmado si la venta lo produjo, o el PDF de la póliza si el endpoint
  // lo devuelve. Así no se ofrece un documento que abriría una pestaña vacía.
  private async refreshPolicyDocumentAvailability(): Promise<void> {
    if (this.assistanceAttachmentId) {
      this.policyDocumentAvailable = true;
      return;
    }

    if (!this.hasIssuedPolicy) {
      this.policyDocumentAvailable = false;
      return;
    }

    this.documentAvailabilityLoading = true;
    try {
      this.policyDocumentAvailable = await this.agricolaService.hasPolicyDocument(this.policyNumber);
    } finally {
      this.documentAvailabilityLoading = false;
    }
  }

  private showEmissionError(message: string): void {
    this.submitting = false;
    this.showBusinessError(message);
    this.mode = 'form';
  }

  private showRequiredFieldsError(): void {
    this.dialog.open(AgricolaPaymentErrorDialogComponent, {
      data: {
        title: 'Faltan datos',
        message: 'Por favor, completa todos los campos obligatorios para continuar.'
      },
      autoFocus: false,
      restoreFocus: true,
      panelClass: 'sis-dialog-panel'
    });
  }

  private showBusinessError(message: string): void {
    this.dialog.open(AgricolaPaymentErrorDialogComponent, {
      data: {
        title: 'No se pudo continuar',
        message
      },
      autoFocus: false,
      restoreFocus: true,
      panelClass: 'sis-dialog-panel'
    });
  }

  private extractNumPoliza(response: unknown): string {
    const candidates = this.flattenResponseCandidates(response);
    let transactionIdFallback = '';

    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') {
        continue;
      }

      const record = candidate as Record<string, unknown>;
      const value = record['policy_number'] || record['policyNumber'] || record['numPoliza'] || record['policy'];
      if (value) {
        return String(value);
      }

      if (!transactionIdFallback) {
        const txId = record['transactionId'] || record['transaction_id'];
        if (txId) {
          transactionIdFallback = String(txId);
        }
      }
    }

    return transactionIdFallback;
  }

  private extractSaleError(response: unknown): string {
    if (!response || typeof response !== 'object') {
      return '';
    }
    const data = (response as Record<string, unknown>)['data'];
    if (data && typeof data === 'object') {
      const error = (data as Record<string, unknown>)['error'];
      if (error && typeof error === 'string') {
        return error;
      }
    }
    return '';
  }

  private extractErrorMessage(error: any): string {
    return error?.error?.upstream_response?.data?.descripcion?.trim() ||
      error?.error?.message ||
      error?.error?.data?.message ||
      error?.error?.data?.error ||
      error?.message ||
      'No se pudo aperturar la póliza. Intenta nuevamente.';
  }

  private flattenResponseCandidates(response: unknown): unknown[] {
    const candidates: unknown[] = [response];
    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      candidates.push(record['data']);
      if (record['data'] && typeof record['data'] === 'object') {
        candidates.push((record['data'] as Record<string, unknown>)['data']);
      }
    }

    return candidates;
  }
}
